import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Calendar, Target, ChevronRight, ChevronLeft, Sparkles, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { apiClient } from '../../api/client';
import { triggerAchievement } from '../engagement/AchievementUnlocked';

/**
 * CompleteProfileWizard — 3-step guided flow for new Google OAuth users.
 * Step 1: Date of Birth (age verification / COPPA)
 * Step 2: Choose a focus area
 * Step 3: Quick intro to AvAs
 */
const FOCUS_AREAS = [
  { id: 'confidence', label: 'Build Confidence', desc: 'Practise speaking up, asserting yourself, and feeling comfortable in social settings', icon: '💪' },
  { id: 'friendships', label: 'Make Friends', desc: 'Learn conversation starters, small talk, and how to deepen connections', icon: '🤝' },
  { id: 'interviews', label: 'Ace Interviews', desc: 'Prepare for job interviews, school presentations, and professional networking', icon: '🎯' },
  { id: 'dating', label: 'Dating & Romance', desc: 'Navigate first dates, expressing feelings, and building romantic confidence', icon: '💜' },
  { id: 'family', label: 'Family Talks', desc: 'Practise difficult conversations with parents, siblings, and family members', icon: '🏠' },
  { id: 'workplace', label: 'Workplace Skills', desc: 'Handle meetings, giving feedback, resolving conflicts, and leadership communication', icon: '💼' },
];

const CompleteProfileWizard = ({ onComplete, onSkip }) => {
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const [step, setStep] = useState(1);
  const [dob, setDob] = useState('');
  const [dobError, setDobError] = useState('');
  const [selectedFocus, setSelectedFocus] = useState('');
  const [saving, setSaving] = useState(false);

  const handleDobNext = () => {
    if (!dob) { setDobError('Please enter your date of birth'); return; }
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
    if (age < 13) { setDobError('You must be at least 13 years old to use HiBFF'); return; }
    setDobError('');
    setStep(2);
  };

  const handleFinish = async () => {
    setSaving(true);
    try {
      // Save DOB and focus area
      const updates = { onboarding_complete: true };
      if (dob) updates.dob = dob;
      if (selectedFocus) updates.focus_area = selectedFocus;
      await apiClient.request('/api/users/me', { method: 'PUT', body: JSON.stringify(updates) });
      try { await updateUser(updates); } catch {}
      localStorage.setItem('hibff_profile_wizard_done', 'true');
      // Fire achievement
      setTimeout(() => triggerAchievement('profile_complete'), 600);
      if (onComplete) onComplete();
    } catch (err) {
      console.error('Failed to save profile:', err);
      if (onComplete) onComplete();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4" data-testid="complete-profile-wizard">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-purple-600 p-6 text-white relative">
          <button onClick={onSkip} className="absolute top-4 right-4 text-white/70 hover:text-white" data-testid="wizard-skip-btn">
            <X size={20} />
          </button>
          <Sparkles size={28} className="mb-2" />
          <h2 className="text-xl font-bold">Complete Your Profile</h2>
          <p className="text-sm text-white/80 mt-1">
            {step === 1 ? 'Step 1 of 3 — Verify your age' : step === 2 ? 'Step 2 of 3 — Choose your focus' : 'Step 3 of 3 — You\'re all set!'}
          </p>
          {/* Progress dots */}
          <div className="flex gap-2 mt-3">
            {[1, 2, 3].map(s => (
              <div key={s} className={`h-1.5 rounded-full transition-all ${s <= step ? 'bg-white w-8' : 'bg-white/30 w-4'}`} />
            ))}
          </div>
        </div>

        {/* Step Content */}
        <div className="p-6">
          {step === 1 && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 text-gray-700 mb-2">
                <Calendar size={20} className="text-orange-500" />
                <span className="font-semibold">When were you born?</span>
              </div>
              <p className="text-sm text-gray-500">We need this for age-appropriate content and COPPA compliance.</p>
              <input
                type="date"
                value={dob}
                onChange={e => { setDob(e.target.value); setDobError(''); }}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent text-gray-700"
                max={new Date().toISOString().split('T')[0]}
                data-testid="wizard-dob-input"
              />
              {dobError && <p className="text-sm text-red-500">{dobError}</p>}
              <div className="flex justify-between pt-2">
                <button onClick={onSkip} className="text-sm text-gray-500 hover:text-gray-700">Skip for now</button>
                <button
                  onClick={handleDobNext}
                  className="flex items-center gap-2 px-6 py-2.5 bg-orange-500 text-white font-semibold rounded-xl hover:bg-orange-600 transition-all"
                  data-testid="wizard-next-btn"
                >
                  Next <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 text-gray-700 mb-2">
                <Target size={20} className="text-purple-500" />
                <span className="font-semibold">What do you want to practise?</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {FOCUS_AREAS.map(f => (
                  <button
                    key={f.id}
                    onClick={() => setSelectedFocus(f.id)}
                    className={`p-3 rounded-xl border-2 text-left transition-all ${
                      selectedFocus === f.id
                        ? 'border-purple-500 bg-purple-50 shadow-md'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                    data-testid={`wizard-focus-${f.id}`}
                  >
                    <span className="text-lg">{f.icon}</span>
                    <p className="text-sm font-semibold text-gray-800 mt-1">{f.label}</p>
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{f.desc}</p>
                  </button>
                ))}
              </div>
              <div className="flex justify-between pt-2">
                <button onClick={() => setStep(1)} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
                  <ChevronLeft size={14} /> Back
                </button>
                <button
                  onClick={() => setStep(3)}
                  className="flex items-center gap-2 px-6 py-2.5 bg-purple-600 text-white font-semibold rounded-xl hover:bg-purple-700 transition-all"
                  data-testid="wizard-next-focus-btn"
                >
                  {selectedFocus ? 'Next' : 'Skip'} <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4 text-center">
              <div className="w-16 h-16 mx-auto bg-gradient-to-br from-orange-400 to-purple-500 rounded-2xl flex items-center justify-center">
                <User size={32} className="text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">You&apos;re ready to go!</h3>
              <p className="text-sm text-gray-600 max-w-sm mx-auto">
                Your AI practice partners (AvAs) are waiting. Start a conversation to build real social skills — one chat at a time.
              </p>
              <div className="flex flex-col gap-3 pt-2">
                <button
                  onClick={handleFinish}
                  disabled={saving}
                  className="w-full py-3 bg-gradient-to-r from-orange-500 to-purple-600 text-white font-bold rounded-xl hover:shadow-lg transition-all disabled:opacity-50"
                  data-testid="wizard-finish-btn"
                >
                  {saving ? 'Saving...' : 'Start Practising'}
                </button>
                <button
                  onClick={() => { handleFinish(); navigate('/conversations'); }}
                  className="text-sm text-purple-600 hover:text-purple-700 font-medium"
                >
                  Go straight to conversations →
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompleteProfileWizard;
